import { useEffect, useRef, useState } from 'react'
import { useGameStore } from '../../store/gameStore'
import Icon from './Icon'

interface SettingsDialogProps {
  open: boolean
  onClose: () => void
}

/** 动画档位：值对应 GameSettings.animationSpeed（slow = 安静关闪） */
const ANIM_OPTIONS = [
  { value: 'slow', label: '安静', desc: '关闭所有闪烁' },
  { value: 'normal', label: '慢闪', desc: '柔和慢速呼吸' },
  { value: 'fast', label: '复古', desc: '经典像素硬闪' },
] as const

/** 字号档位：值对应 GameSettings.fontSize，html 根字号随档缩放（见 index.css） */
const FONT_OPTIONS = [
  { value: 'small', label: '小', preview: '12px' },
  { value: 'medium', label: '标准', preview: '15px' },
  { value: 'large', label: '大', preview: '19px' },
] as const

type DialogKind = 'unlock' | 'clearLearning' | 'resetAll' | null

/**
 * 二次确认的文案 + 主题色 + 图标 —— 三种危险操作共用 alertdialog 框架
 * 通过 dialog 配置对象驱动, 避免重复 JSX
 */
const DIALOG_CONFIG: Record<Exclude<DialogKind, null>, {
  title: string
  body: string
  iconName: 'warn' | 'cross'
  confirmLabel: string
  danger: boolean  // true = 红色强提示; false = 中性色
}> = {
  unlock: {
    title: '确认解锁',
    body: '你真的都学懂了吗？解锁后所有章节将立即可用。',
    iconName: 'warn',
    confirmLabel: '确认解锁',
    danger: true,
  },
  clearLearning: {
    title: '清空学习数据',
    body: '将清空所有学习进度、勋章、错题本、统计、上次学习位置。但保留：答案之书 ★ 收藏、茶馆留言、设置偏好。',
    iconName: 'warn',
    confirmLabel: '确认清空',
    danger: false,
  },
  resetAll: {
    title: '全部重置（不可撤销）',
    body: '将永久清空全部数据，包括答案之书历史/收藏、茶馆留言。此操作一旦执行无法恢复。',
    iconName: 'cross',
    confirmLabel: '我已了解，确认全部清空',
    danger: true,
  },
}

/**
 * ⚙ 设置弹窗 —— 接通既有的 GameSettings 数据模型（音效 / 动画档位 / 字号）。
 * 设置即时生效并自动持久化到 IndexedDB 存档；提供「解锁全部章节 / 清空学习 / 全部重置」三个危险操作, 各有二次确认。
 */
function SettingsDialog({ open, onClose }: SettingsDialogProps) {
  const settings = useGameStore(s => s.settings)
  const updateSettings = useGameStore(s => s.updateSettings)
  const unlockAllChapters = useGameStore(s => s.unlockAllChapters)
  const clearLearning = useGameStore(s => s.clearLearning)
  const resetAll = useGameStore(s => s.resetAll)
  const doneRef = useRef<HTMLButtonElement>(null)
  const [confirmKind, setConfirmKind] = useState<DialogKind>(null)

  // 打开时聚焦「完成」按钮；Esc 关闭（优先关闭弹出的确认对话框）
  useEffect(() => {
    if (!open) return
    doneRef.current?.focus()
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (confirmKind) {
          setConfirmKind(null)
        } else {
          onClose()
        }
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onClose, confirmKind])

  if (!open) return null

  const handleConfirm = () => {
    if (confirmKind === 'unlock') {
      unlockAllChapters()
    } else if (confirmKind === 'clearLearning') {
      clearLearning()
    } else if (confirmKind === 'resetAll') {
      resetAll()
    }
    setConfirmKind(null)
    onClose()
  }

  const cfg = confirmKind ? DIALOG_CONFIG[confirmKind] : null

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-label="设置"
    >
      {/* 遮罩，点击关闭 */}
      <div className="absolute inset-0 bg-black/75" onClick={onClose} />

      <div className="relative pixel-box pixel-box-bright w-full max-w-sm p-4">
        {/* 标题行 */}
        <div className="flex items-center justify-between mb-3">
          <div className="font-pixel text-[11px] text-[color:var(--color-gold)] tracking-wider inline-flex items-center gap-1.5">
            <Icon name="gear" size={13} className="transition-transform duration-500 hover:rotate-90" />
            设置
          </div>
          <button
            onClick={onClose}
            aria-label="关闭设置"
            className="text-[color:var(--color-text-dim)] hover:text-[color:var(--color-bear)] transition-colors px-1"
          >
            <Icon name="cross" size={11} />
          </button>
        </div>

        {/* 音效开关 */}
        <div className="mb-4">
          <div className="font-pixel text-[9px] text-[color:var(--color-cyan)] tracking-wider mb-2">
            ► 音效
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => updateSettings({ soundEnabled: true })}
              aria-pressed={settings.soundEnabled}
              className={`flex-1 pixel-box p-2 text-center transition-colors ${
                settings.soundEnabled
                  ? 'border-[color:var(--color-gold)] bg-[color:var(--color-bg-elevated)]'
                  : 'opacity-60 hover:opacity-100'
              }`}
            >
              <div
                className={`font-pixel text-[9px] ${
                  settings.soundEnabled ? 'text-[color:var(--color-gold)]' : 'text-[color:var(--color-text)]'
                }`}
              >
                开启 🔊
              </div>
            </button>
            <button
              onClick={() => updateSettings({ soundEnabled: false })}
              aria-pressed={!settings.soundEnabled}
              className={`flex-1 pixel-box p-2 text-center transition-colors ${
                !settings.soundEnabled
                  ? 'border-[color:var(--color-gold)] bg-[color:var(--color-bg-elevated)]'
                  : 'opacity-60 hover:opacity-100'
              }`}
            >
              <div
                className={`font-pixel text-[9px] ${
                  !settings.soundEnabled ? 'text-[color:var(--color-gold)]' : 'text-[color:var(--color-text)]'
                }`}
              >
                静音 🔇
              </div>
            </button>
          </div>
        </div>

        {/* 动画档位 */}
        <div className="mb-4">
          <div className="font-pixel text-[9px] text-[color:var(--color-cyan)] tracking-wider mb-2">
            ► 动画
          </div>
          <div className="flex gap-2">
            {ANIM_OPTIONS.map(o => {
              const selected = settings.animationSpeed === o.value
              return (
                <button
                  key={o.value}
                  onClick={() => updateSettings({ animationSpeed: o.value })}
                  aria-pressed={selected}
                  className={`flex-1 pixel-box p-2 text-left transition-colors ${
                    selected
                      ? 'border-[color:var(--color-gold)] bg-[color:var(--color-bg-elevated)]'
                      : 'opacity-60 hover:opacity-100'
                  }`}
                >
                  <div
                    className={`font-pixel text-[9px] ${
                      selected ? 'text-[color:var(--color-gold)]' : 'text-[color:var(--color-text)]'
                    }`}
                  >
                    {o.label}
                  </div>
                  <div className="font-pixel-body text-[11px] text-[color:var(--color-text-dim)] mt-1 leading-snug">
                    {o.desc}
                  </div>
                </button>
              )
            })}
          </div>
        </div>

        {/* 字号档位 */}
        <div className="mb-4">
          <div className="font-pixel text-[9px] text-[color:var(--color-cyan)] tracking-wider mb-2">
            ► 字号
          </div>
          <div className="flex gap-2">
            {FONT_OPTIONS.map(o => {
              const selected = settings.fontSize === o.value
              return (
                <button
                  key={o.value}
                  onClick={() => updateSettings({ fontSize: o.value })}
                  aria-pressed={selected}
                  className={`flex-1 pixel-box p-2 flex items-center justify-center gap-2 transition-colors ${
                    selected
                      ? 'border-[color:var(--color-gold)] bg-[color:var(--color-bg-elevated)]'
                      : 'opacity-60 hover:opacity-100'
                  }`}
                >
                  <span
                    className={`font-pixel-body leading-none ${
                      selected ? 'text-[color:var(--color-gold)]' : 'text-[color:var(--color-text)]'
                    }`}
                    style={{ fontSize: o.preview }}
                  >
                    A
                  </span>
                  <span
                    className={`font-pixel text-[9px] ${
                      selected ? 'text-[color:var(--color-gold)]' : 'text-[color:var(--color-text)]'
                    }`}
                  >
                    {o.label}
                  </span>
                </button>
              )
            })}
          </div>
        </div>

        {/* 底部说明 + 完成 */}
        <div className="font-pixel-display text-[9px] text-[color:var(--color-text-dim)] mb-3">
          ► 设置即时生效，自动存入本地存档
        </div>
        <button
          ref={doneRef}
          onClick={onClose}
          className="pixel-button pixel-button-primary w-full"
        >
          ► 完成
        </button>

        {/* 危险操作区 —— 解锁 / 清空 / 全部重置 */}
        <div className="mt-4 pt-3 border-t border-[color:var(--color-border)] space-y-2">
          <div className="font-pixel-display text-[9px] text-[color:var(--color-text-dim)] mb-2">
            ► 危险操作
          </div>
          <button
            onClick={() => setConfirmKind('unlock')}
            className="w-full flex items-center justify-center gap-2 px-3 py-2 font-pixel text-[10px] text-[color:var(--color-bear)] border border-[color:var(--color-bear)] hover:bg-[color:var(--color-bear)] hover:text-white transition-colors"
          >
            <Icon name="lock" size={12} />
            解锁全部章节
          </button>
          <button
            onClick={() => setConfirmKind('clearLearning')}
            className="w-full flex items-center justify-center gap-2 px-3 py-2 font-pixel text-[10px] text-[color:var(--color-cyan)] border border-[color:var(--color-cyan)] hover:bg-[color:var(--color-cyan)] hover:text-white transition-colors"
          >
            <Icon name="broom" size={12} />
            清空学习数据
          </button>
          <button
            onClick={() => setConfirmKind('resetAll')}
            className="w-full flex items-center justify-center gap-2 px-3 py-2 font-pixel text-[10px] text-[color:var(--color-bear)] border border-[color:var(--color-bear)] hover:bg-[color:var(--color-bear)] hover:text-white transition-colors"
          >
            <Icon name="cross" size={12} />
            全部重置
          </button>
        </div>
      </div>

      {/* 二次确认对话框 —— 三种危险操作共用 */}
      {cfg && (
        <div
          className="absolute inset-0 z-10 flex items-center justify-center p-4"
          role="alertdialog"
          aria-modal="true"
          aria-label={cfg.title}
        >
          <div className="absolute inset-0 bg-black/80" onClick={() => setConfirmKind(null)} />
          <div
            className="relative pixel-box w-full max-w-xs p-4"
            style={cfg.danger ? { borderColor: 'var(--color-bear)' } : { borderColor: 'var(--color-cyan)' }}
          >
            <div className="flex items-center gap-2 mb-3">
              <Icon
                name={cfg.iconName}
                size={14}
                className={cfg.danger ? 'text-[color:var(--color-bear)]' : 'text-[color:var(--color-cyan)]'}
              />
              <span
                className={`font-pixel text-[11px] tracking-wider ${
                  cfg.danger ? 'text-[color:var(--color-bear)]' : 'text-[color:var(--color-cyan)]'
                }`}
              >
                {cfg.title}
              </span>
            </div>
            <p className="font-pixel-body text-[12px] text-[color:var(--color-text)] leading-relaxed mb-4">
              {cfg.body}
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setConfirmKind(null)}
                className="flex-1 pixel-button pixel-button-primary"
              >
                ► 再想想
              </button>
              <button
                onClick={handleConfirm}
                className={`flex-1 px-3 py-1.5 font-pixel text-[10px] text-white border transition-opacity hover:opacity-80 ${
                  cfg.danger
                    ? 'border-[color:var(--color-bear)] bg-[color:var(--color-bear)]'
                    : 'border-[color:var(--color-cyan)] bg-[color:var(--color-cyan)]'
                }`}
              >
                ► {cfg.confirmLabel}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default SettingsDialog
